import { useTranslation } from 'react-i18next';

import { useCart } from '@sola/cart-sdk/store.js';
import { formatNumberAsCurrencyString } from '@sola/cart-sdk/utils/amount.js';
import { generalConfig } from '@sola/config/lib/getters.js';
import i18n from '@sola/locales/index.js';
import { CartItemTypeEnum } from '@sola/schema/dotcom/cart/cart-item.js';
import { CommitmentFrequencyEnum } from '@sola/schema/dotcom/cart/commitments.js';
import { ErrorCart } from '@sola/schema/dotcom/cart/errors.js';

import { useDonate } from '../donate-context';
import { DonateActionEnum, DonateFrequency } from '../donate.types';
import { useDonateValidation } from './use-donate-validation';

export const useDonateActions = () => {
  const { state, props, dispatch, refs } = useDonate();
  const { getCurrentAmount } = useDonateValidation();
  const {
    actions: { addItems, goToCheckout },
  } = useCart('actions');

  const { t } = useTranslation(['checkout']);

  const handleFrequencyChange = (frequency: DonateFrequency) => {
    dispatch({ type: DonateActionEnum.SET_FREQUENCY, payload: frequency });

    if (!state.selectedAmounts[frequency]?.length) {
      dispatch({ type: DonateActionEnum.SET_OTHER_SELECTED, payload: true });
    }
  };

  const handleAmountSelect = (amount: string) => {
    if (!state.donateFrequency) {
      return;
    }
    if (amount === 'other') {
      dispatch({ type: DonateActionEnum.SET_OTHER_SELECTED, payload: true });
      setTimeout(() => {
        refs[state.donateFrequency].current?.focus();
      }, 0);
      return;
    }
    dispatch({
      type: DonateActionEnum.SET_AMOUNT,
      payload: { frequency: state.donateFrequency, amount },
    });
    dispatch({ type: DonateActionEnum.SET_OTHER_SELECTED, payload: false });
  };

  const handleOtherAmountChange = (amount: string) => {
    if (!state.donateFrequency) {
      return;
    }
    dispatch({
      type: DonateActionEnum.SET_OTHER_AMOUNT,
      payload: { frequency: state.donateFrequency, amount },
    });
  };

  const handleInputFocus = (_e: React.FocusEvent<HTMLInputElement>) => {};

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const numericAmount = Number(e.target.value);
    e.target.value = numericAmount
      ? formatNumberAsCurrencyString(numericAmount)
      : '';
  };

  const handleCartErrors = (errors: ErrorCart[]) => {
    // We ignore the userShouldFix errors as those are handled in checkout
    const errorsToDisplay = errors.filter((error) => !error.userShouldFix);

    if (errorsToDisplay.length === 0) {
      return false;
    }

    const firstError = errorsToDisplay[0];

    let errorMessage = t('checkout:errors.ITEMS_ADD.message');

    if (i18n.exists(`checkout:errors.${firstError.type}.message`)) {
      errorMessage = t(`checkout:errors.${firstError.type}.message`);
    }

    dispatch({ type: DonateActionEnum.SET_SUBMITTING, payload: false });
    dispatch({
      type: DonateActionEnum.SET_SUBMIT_ERROR,
      payload: errorMessage,
    });

    return true;
  };

  const handleSubmit = async () => {
    dispatch({ type: DonateActionEnum.SET_SUBMITTING, payload: true });
    dispatch({ type: DonateActionEnum.SET_SUBMIT_ERROR, payload: null });

    try {
      const commitmentFrequency =
        state.donateFrequency as unknown as CommitmentFrequencyEnum;
      const amount = getCurrentAmount();
      const numericAmount = Number(amount);

      let minNotMet = false;
      // TODO: this will become a stale value and we'll need to find a way to get this from the SDG
      minNotMet = numericAmount < generalConfig.donation.amountMin;

      if (minNotMet) {
        dispatch({
          type: DonateActionEnum.SET_SUBMIT_ERROR,
          payload: {
            title: {
              key: 'checkout:errors.ITEM_MIN.title',
              payload: {
                minLimit: Number(generalConfig.donation.amountMin ?? 0),
              },
            },
            message: {
              key: 'checkout:errors.ITEM_MIN.message',
            },
          },
        });
        dispatch({ type: DonateActionEnum.SET_SUBMITTING, payload: false });

        return;
      }

      const itemId = props?.fund?.tcmId || props?.fund?.id || '';

      const errors = await addItems({
        items: {
          itemType: CartItemTypeEnum.Fund,
          commitmentFrequency,
          unitPrice: numericAmount,
          fund: {
            cmsId: itemId,
          },
        },
      });

      if (errors?.length && handleCartErrors(errors)) {
        return;
      }

      // @TODO: modal how item was added to the cart
      goToCheckout();
    } catch (error) {
      dispatch({
        type: DonateActionEnum.SET_SUBMIT_ERROR,
        payload: error instanceof Error ? error.message : 'An error occurred',
      });
    } finally {
      // Safari (and sometimes Chrome) doesn't always reset the button state if the user hits the back button from the Checkout page so we need to trigger
      // it here. The timeout of 1ms pushed the event to the end of the call stack, preventing a flicker on the button's state before navigation.
      setTimeout(() => {
        dispatch({ type: DonateActionEnum.SET_SUBMITTING, payload: false });
      }, 1);
    }
  };

  return {
    handleFrequencyChange,
    handleAmountSelect,
    handleOtherAmountChange,
    handleInputFocus,
    handleInputBlur,
    handleSubmit,
  };
};
